import React from 'react';
import ReactModal from 'react-modal';
import { uniqueId } from 'lodash';

// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries -- TODO(strict-boundaries): legacy violation, remove after refactor
import { useKeyboardOpenModalsRegistry } from '@neptune/shared/keyboard-business-logic';

import { bemBlock } from '../../modules/bem';
import { Card } from '../card/Card';

import ModalBody from './ModalBody';
import ModalButton from './ModalButton';
import ModalClose from './ModalClose';
import ModalFooter from './ModalFooter';
import ModalHeader from './ModalHeader';
import ModalText from './ModalText';

import './Modal.less';

export type ModalProps = ReactModal.Props & {
  children?: React.ReactNode;
  className?: string;
  contentClassName?: string;
  data?: { [key: string]: string | undefined };
  variant?: 'full' | 'simple';
  size?: 'sm' | 'md' | 'lg' | 'adaptive';
  span?: 'greedy' | 'shrink' | 'grow';
  withBackdrop?: boolean;
  onClose?: () => void;
  shouldCloseOnEsc?: boolean;
  version?: 1 | 2;
};

const block = bemBlock('n-modal');

function ModalComponent({
  children,
  className,
  contentClassName,
  isOpen = false,
  variant = 'full',
  size = 'md',
  span = 'shrink',
  withBackdrop = true,
  onClose,
  shouldCloseOnEsc = true,
  version = 1,
  ...restProps
}: ModalProps) {
  const modalIdRef = React.useRef<string>(uniqueId('modal-'));
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!shouldCloseOnEsc || event.key !== 'Escape') {
        return;
      }

      onClose?.();
      event.stopPropagation();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose, shouldCloseOnEsc]);

  const registerOpenModal = useKeyboardOpenModalsRegistry();

  React.useEffect(
    function manageKeyboardOpenModalsRegistry() {
      if (!isOpen) {
        return;
      }

      return registerOpenModal(modalIdRef.current);
    },
    [isOpen, registerOpenModal],
  );

  // Will prevent rendering ReactModalPortal
  // this will assure, that modals are displayed in order they are opened
  if (!isOpen) {
    return null;
  }

  const cssClass = block({
    extra: [className],
    modifiers: {
      simple: variant === 'simple',
      size,
      version,
    },
  });

  return (
    <ReactModal
      className={{
        base: cssClass,
        afterOpen: '',
        beforeClose: '',
      }}
      isOpen={isOpen}
      overlayClassName={{
        base: block({ element: 'overlay', modifiers: { 'with-backdrop': withBackdrop } }),
        afterOpen: block({ element: 'overlay', modifiers: ['after-open'] }),
        beforeClose: block({ element: 'overlay', modifiers: ['before-close'] }),
      }}
      contentLabel="neptune-modal" // even though we don't use aria, this prop is required
      ariaHideApp={false}
      closeTimeoutMS={150}
      onRequestClose={onClose}
      {...restProps}
    >
      <Card className={block({ element: 'card', extra: contentClassName })} span={span}>
        {children}
      </Card>
    </ReactModal>
  );
}

export const Modal = Object.assign(ModalComponent, {
  Body: ModalBody,
  Button: ModalButton,
  Close: ModalClose,
  Footer: ModalFooter,
  Header: ModalHeader,
  Text: ModalText,
});
